import type { LoginResponse, ViewerResponse } from '@cyphex/types';
import { apiGet, apiPost } from '@/lib/api/client';
import type { AuthApi } from './contract';

/**
 * Live adapter for `/v1/auth`.
 *
 * The refresh token never appears here: the API sets it as an httpOnly cookie
 * and this code could not read it if it wanted to. Logout needs no CSRF header
 * because it authenticates with `Authorization: Bearer`, which a cross-site
 * page cannot forge — only the cookie-driven /auth/refresh does.
 */
export const httpAuthApi: AuthApi = {
  login: (input) => apiPost<LoginResponse>('/auth/login', { platform: 'web', ...input }),

  verifyMfa: (input) => apiPost<LoginResponse>('/auth/mfa/verify', input),

  register: (input) => apiPost<LoginResponse>('/auth/register', input),

  /**
   * Ends the Cyphex session, and — when the session came from an identity
   * provider — reports where the browser must go next to end the session THERE
   * too.
   *
   * The order matters. `/auth/sso/logout` needs a live access token to find out
   * which provider issued this session, so it is asked FIRST; only then is the
   * session revoked. Asking afterwards would return nothing, and the person
   * would be signed out of Cyphex while still signed in at the IdP — which on a
   * shared machine means the next visitor is signed straight back in.
   *
   * A provider that does not support RP-initiated logout answers `null`, and a
   * failure here is swallowed: sign-out must never be blocked by it.
   */
  async logout() {
    const endSessionUrl = await apiGet<{ endSessionUrl: string | null }>('/auth/sso/logout')
      .then((payload) => payload.endSessionUrl)
      .catch(() => null);

    await apiPost<{ ok: boolean }>('/auth/logout');
    return endSessionUrl === null ? undefined : { endSessionUrl };
  },

  viewer: (signal) => apiGet<ViewerResponse>('/me', signal),
};
